// Settings (02-DESIGN/03-console/01-the-console.md § the surfaces, § conventions:
// tabs), in three tabs. *Workspace*: the workspace's name, which an admin
// changes here (01-tenants-and-roles.md § the account), its address — the
// slug, which is in the address bar and here and nowhere else a person reads,
// and never changes — its plan as the platform set it, read on
// identity.tenant.get (02-the-auth-service.md § account administration; the
// verb answers an admin and refuses a member), and *managed by* its parent
// where it has one. *Sign-in*: the one provider the workspace signs in with,
// its set-up and the switch (§ Settings: the Sign-in tab; decision 0055),
// an admin's; the former Sign-in page's address opens it. Each tab's forms —
// the set-up, the switch, *Collect*'s share, *Present* — stand in the tab's
// place, under the page's title and the tabs; another tab closes the form.
// *Zero-trust*: the
// workspace's recovery shares (§ Settings: the Zero-trust tab;
// 02-secrets/03-escrow.md), an admin's. *Plan*: what the workspace's plan
// includes and allows, and *Apply a plan key* (§ Settings: the Plan tab;
// 02-DESIGN/17-plans/02-features-and-limits.md § the console), an admin's;
// a link from a refusal a plan caused or from Home opens it. *Billing*, where
// the platform profile says the install bills: the plans, the add-ons, the
// details, the payment method, the receipts and *Cancel*
// (02-DESIGN/18-billing/02-the-billing-page.md), an admin's; the address
// `settings/billing` opens it.

import { useState } from "react";
import { useLocation } from "react-router";

import { consoleAddress } from "../../platform/names.ts";
import type { TenantProfile } from "../../platform/profile.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  Field,
  NotAllowed,
  PageHead,
  Pending,
  TextInput,
  useLoad,
  when,
  type Load,
} from "../../ui/components.tsx";
import {
  Subjects,
  validDisplayName,
  type SettingsPutRequest,
  type SettingsReply,
  type TenantReply,
} from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import {
  Code as BlueprintCode,
  SERVICE as BLUEPRINT,
  Subjects as BlueprintSubjects,
  type Entry,
  type StatusReply,
} from "../../wire/blueprint.ts";
import { isRefusal } from "../../platform/bus.ts";
import { useFormState } from "../../ui/form.tsx";
import { Tabs, useTab, type TabSpec } from "../../ui/tabs.tsx";
import { useSurface } from "../surface.tsx";
import { childName, managedBy } from "../workspaces/relation.ts";
import { appliedLine, attest, type Attested } from "../workspaces/blueprint.ts";
import { hasProof } from "../../signin/tokens.ts";
import { OpenPlan, PLAN_TAB } from "./PlanNotes.tsx";
import { BillingTab } from "./BillingTab.tsx";
import { PlanTab } from "./PlanTab.tsx";
import { SignInTab } from "./SignInTab.tsx";
import { BILLING_TAB, initialTab, type Tab } from "./tabs.ts";
import { CollectAction, useZeroTrust, ZeroTrust, zeroTrustFormDrawn } from "./ZeroTrust.tsx";

const WORKSPACE: TabSpec<Tab> = { id: "workspace", label: "Workspace" };
const SIGN_IN: TabSpec<Tab> = { id: "sign-in", label: "Sign-in" };
const ZERO_TRUST: TabSpec<Tab> = { id: "zero-trust", label: "Zero-trust" };
const PLAN: TabSpec<Tab> = { id: PLAN_TAB, label: "Plan" };
const BILLING: TabSpec<Tab> = { id: BILLING_TAB, label: "Billing" };

/**
 * Blueprint is the workspace's own blueprint as its admin reads it on
 * Settings (02-DESIGN/13-blueprints/02-convergence.md § the console in the
 * child; decision 0064): *Managed by Acme*, the revision written, and the
 * revision applied from the workspace's own report, which the page verifies
 * against the carrier keys it holds; nothing to edit about it.
 */
function Blueprint({ slug }: { slug: string }) {
  const s = useSurface();
  const [own] = useLoad("own-blueprint", () =>
    s.bus.call<Entry>(BLUEPRINT, BlueprintSubjects.ownGet(), undefined),
  );
  const [attested] = useLoad("own-status", async (): Promise<Attested> => {
    const status = await s.bus
      .call<StatusReply>(BLUEPRINT, BlueprintSubjects.ownStatus(), undefined)
      .catch((err: unknown) => {
        if (isRefusal(err, BlueprintCode.notFound)) {
          return undefined;
        }
        throw err;
      });
    return attest(status, await s.bus.carrierKeys(), slug);
  });
  return (
    <>
      <dt>Blueprint</dt>
      <dd>
        {own.state === "ready"
          ? `version ${String(own.value.revision)}${own.value.created !== undefined ? `, written ${when(own.value.created)}` : ""}`
          : own.state === "pending"
            ? "reading…"
            : "not readable here"}
        {own.state === "ready" && attested.state === "ready" && (
          <> · {appliedLine(own.value.revision, attested.value, when)}</>
        )}
        <div className="small faint">
          What the workspace must look like, kept true by its steward. It is changed only where it is written.
        </div>
      </dd>
    </>
  );
}

export function SettingsPage({ tenant }: { tenant: TenantProfile }) {
  const s = useSurface();
  const admin = s.offered("settings");
  // The Billing tab is drawn only where the install bills, and only for an admin.
  const bills = usePage().platform.billing === true;
  const tabs = admin
    ? bills
      ? [WORKSPACE, SIGN_IN, ZERO_TRUST, PLAN, BILLING]
      : [WORKSPACE, SIGN_IN, ZERO_TRUST, PLAN]
    : [WORKSPACE];
  const location = useLocation();
  const [tab, setTabNow] = useTab(tabs, initialTab(location.state, hasProof()));
  const form = useFormState();
  // Another tab closes the form standing in this one.
  const setTab = (t: Tab) => {
    if (form.open !== undefined) {
      form.close();
    }
    setTabNow(t);
  };
  const [held] = useLoad("tenant", () => s.bus.call<TenantReply>(SERVICE, Subjects.tenantGet(), undefined));
  const z = useZeroTrust();
  const parent =
    held.state === "ready" && held.value.tenant.parent !== undefined
      ? childName(held.value.tenant.parent)
      : undefined;
  return (
    <>
      <PageHead
        title="Settings"
        line={`${s.name}'s name, address and plan, how people sign in, and who can rebuild its keys.`}
        action={
          tab === "zero-trust" &&
          !zeroTrustFormDrawn(
            form.open,
            z.collected !== undefined,
            z.st.state === "ready" ? z.st.value : undefined,
          ) ? (
            <CollectAction z={z} />
          ) : undefined
        }
      />
      <OpenPlan.Provider
        value={
          admin
            ? () => {
                setTab(PLAN_TAB);
              }
            : undefined
        }
      >
        <Tabs label="Settings" tabs={tabs} value={tab} onChange={setTab}>
          {tab === "workspace" && <Workspace tenant={tenant} held={held} />}
          {tab === "sign-in" && <SignInTab />}
          {tab === "zero-trust" && <ZeroTrust z={z} holders={{ name: s.name, parent }} />}
          {tab === PLAN_TAB && <PlanTab held={held} bills={bills} />}
          {tab === BILLING_TAB && <BillingTab held={held} />}
        </Tabs>
      </OpenPlan.Provider>
    </>
  );
}

/** Workspace is the first tab: the name, the address, the plan, and *managed by*. */
function Workspace({ tenant, held }: { tenant: TenantProfile; held: Load<TenantReply> }) {
  const { origin } = usePage();
  return (
    <div className="card">
      <dl className="kv">
        <dt>Name</dt>
        <dd>
          <Name />
        </dd>
        <dt>Address</dt>
        <dd className="mono">{consoleAddress(origin, tenant.slug)}</dd>
        <dt>Plan</dt>
        <dd>
          {held.state === "pending" && <Pending what="Reading" />}
          {held.state === "refused" && <NotAllowed err={held.err} what="Plan" />}
          {held.state === "ready" && (
            <>
              {held.value.tenant.plan}
              <div className="small faint">As the platform set it; not changed here.</div>
            </>
          )}
        </dd>
        {held.state === "ready" && managedBy(held.value.tenant) !== undefined && (
          <>
            <dt>Relation</dt>
            <dd>
              {managedBy(held.value.tenant)}
              <div className="small faint">The workspace that manages this one.</div>
            </dd>
          </>
        )}
        {held.state === "ready" && held.value.tenant.managed === true && <Blueprint slug={tenant.slug} />}
      </dl>
    </div>
  );
}

/**
 * Name shows the workspace's name and, for an admin, changes it: the service
 * keeps the new name, and the shell shows it everywhere at once.
 */
function Name() {
  const s = useSurface();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const trimmed = draft.trim();
  const valid = validDisplayName(trimmed);
  const save = () => {
    if (!valid || busy) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const body: SettingsPutRequest = { name: trimmed };
    s.bus.call<SettingsReply>(SERVICE, Subjects.settingsPut(), body).then(
      (reply) => {
        setBusy(false);
        setEditing(false);
        s.rename(reply.name);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  if (!editing) {
    return (
      <>
        {s.name}
        {s.offered("settings") && (
          <>
            {" "}
            <button
              type="button"
              className="text-link small"
              onClick={() => {
                setDraft(s.name);
                setEditing(true);
              }}
            >
              Change
            </button>
          </>
        )}
      </>
    );
  }
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      {err !== undefined && <NotAllowed err={err} what="Save" />}
      <Field label="Name" hint="What people call this workspace. Its address stays as it is.">
        <TextInput value={draft} onChange={setDraft} placeholder="Acme" autoFocus disabled={busy} />
      </Field>
      <div className="rows">
        <Button type="submit" kind="primary" disabled={!valid || busy}>
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button
          kind="quiet"
          disabled={busy}
          onClick={() => {
            setEditing(false);
            setErr(undefined);
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
